import { Change, DiffResult } from '../models/diff-result';

export class DiffHighlighter {
  /**
   * Main comparison method that compares two texts and returns highlighted HTML
   */
  static compareText(text1: string, text2: string): DiffResult {
    const lines1 = text1.split('\n');
    const lines2 = text2.split('\n');

    const diff = this.computeDiff(lines1, lines2);

    const leftHighlighted = this.generateHighlightedHtml(
      diff.leftChanges,
      'left',
    );
    const rightHighlighted = this.generateHighlightedHtml(
      diff.rightChanges,
      'right',
    );

    return {
      leftHighlighted,
      rightHighlighted,
      hasChanges: diff.hasChanges,
    };
  }

  /**
   * Compute diff between two arrays of lines using LCS algorithm
   */
  private static computeDiff(
    lines1: string[],
    lines2: string[],
  ): {
    leftChanges: Change[];
    rightChanges: Change[];
    hasChanges: boolean;
  } {
    const lcs = this.longestCommonSubsequence(lines1, lines2);
    const leftChanges: Change[] = [];
    const rightChanges: Change[] = [];
    let hasChanges = false;

    let i = 0;
    let j = 0;
    let lineNum = 0;

    while (i < lines1.length || j < lines2.length) {
      lineNum++;

      if (i < lines1.length && j < lines2.length && lines1[i] === lines2[j]) {
        // Lines match - unchanged
        leftChanges.push({
          lineNumber: lineNum,
          type: 'unchanged',
          content: lines1[i],
        });
        rightChanges.push({
          lineNumber: lineNum,
          type: 'unchanged',
          content: lines2[j],
        });
        i++;
        j++;
      } else if (
        i < lines1.length &&
        !this.isInLCS(lines1[i], j, lines2, lcs)
      ) {
        // Line only in left - removed
        leftChanges.push({
          lineNumber: lineNum,
          type: 'removed',
          content: lines1[i],
        });
        rightChanges.push({
          lineNumber: lineNum,
          type: 'removed',
          content: '',
        });
        hasChanges = true;
        i++;
      } else if (
        j < lines2.length &&
        !this.isInLCS(lines2[j], i, lines1, lcs)
      ) {
        // Line only in right - added
        leftChanges.push({
          lineNumber: lineNum,
          type: 'added',
          content: '',
        });
        rightChanges.push({
          lineNumber: lineNum,
          type: 'added',
          content: lines2[j],
        });
        hasChanges = true;
        j++;
      } else {
        // Lines differ - modified
        const content1 = i < lines1.length ? lines1[i] : '';
        const content2 = j < lines2.length ? lines2[j] : '';

        leftChanges.push({
          lineNumber: lineNum,
          type: 'modified',
          content: content1,
        });
        rightChanges.push({
          lineNumber: lineNum,
          type: 'modified',
          content: content2,
        });
        hasChanges = true;
        i++;
        j++;
      }
    }

    return { leftChanges, rightChanges, hasChanges };
  }

  /**
   * Check if a line is in the LCS at a given position
   */
  private static isInLCS(
    line: string,
    startPos: number,
    otherLines: string[],
    lcs: string[],
  ): boolean {
    return lcs.includes(line);
  }

  /**
   * Compute Longest Common Subsequence using dynamic programming
   */
  private static longestCommonSubsequence(
    lines1: string[],
    lines2: string[],
  ): string[] {
    const m = lines1.length;
    const n = lines2.length;
    const dp: number[][] = Array(m + 1)
      .fill(0)
      .map(() => Array(n + 1).fill(0));

    // Build LCS table
    for (let i = 1; i <= m; i++) {
      for (let j = 1; j <= n; j++) {
        if (lines1[i - 1] === lines2[j - 1]) {
          dp[i][j] = dp[i - 1][j - 1] + 1;
        } else {
          dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
        }
      }
    }

    // Backtrack to find LCS
    const lcs: string[] = [];
    let i = m;
    let j = n;

    while (i > 0 && j > 0) {
      if (lines1[i - 1] === lines2[j - 1]) {
        lcs.unshift(lines1[i - 1]);
        i--;
        j--;
      } else if (dp[i - 1][j] > dp[i][j - 1]) {
        i--;
      } else {
        j--;
      }
    }

    return lcs;
  }

  /**
   * Generate highlighted HTML from changes
   */
  private static generateHighlightedHtml(
    changes: Change[],
    side: 'left' | 'right',
  ): string {
    const lines = changes.map((change) => {
      const cssClass = `diff-${change.type}`;
      const content = this.escapeHtml(change.content || ' ');
      return `<div class="${cssClass}">${content}</div>`;
    });

    return lines.join('');
  }

  /**
   * Escape HTML special characters to prevent XSS
   */
  private static escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}
